import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { memoryStore, useStore } from "../../lib/store";
import { apiClient, useApiQuery } from "../../services/api";
import { roomEntryWelcome } from "../../domains/partners/roomWelcome";
import { isRoomContentPath } from "../../domains/room/utils/roomContentPath";
import {
  reloadCurrentUser,
  useCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import type { RoomDiscoveryChannel } from "../../tools/analytics/domains/invite.analytics";
import { useIsDesktopView } from "../../common/hooks/useIsDesktopView";
import { displayToast } from "../app/AppToast";
import { RoomWelcomeSheet } from "./RoomWelcomeSheet";

// The welcome sheet on screen, if any. One at a time: a second open replaces it.
const roomWelcomeStore = memoryStore<{
  roomId: string;
  roomDiscoveryChannel: RoomDiscoveryChannel | null;
} | null>(null);

// The rooms whose welcome this session already opened. One opening per room:
// a failed "seen" write, or a room that fails to load, must not reopen the
// sheet at every entry. A room that skipped is not in it: the next entry
// decides again, for a member made since then.
const checkedRoomsStore = memoryStore<ReadonlySet<string>>(new Set());

function markRoomChecked(roomId: string): void {
  checkedRoomsStore.set((rooms) => new Set(rooms).add(roomId));
}

/**
 * Opens the welcome sheet of a room the user just joined, over whatever page
 * they are on (docs/partner-objection.md, REQ-05): a Follow in the room, on a
 * shared article or from a suggestion, a QR code, an invitation, a signup.
 * The caller records the join first.
 */
function openRoomWelcome(
  roomId: string,
  roomDiscoveryChannel: RoomDiscoveryChannel | null
): void {
  markRoomChecked(roomId);
  roomWelcomeStore.set({ roomId, roomDiscoveryChannel });
}

/**
 * Drops a welcome still asked for: at logout, so that the next account on
 * this tab never sees it.
 */
function clearRoomWelcome(): void {
  roomWelcomeStore.set(null);
  checkedRoomsStore.set(new Set());
}

/**
 * Opens, at the entry into a room, the welcome sheet that the member never saw
 * (docs/partner-objection.md, REQ-14). The joins open their own sheet, so a
 * member made by the server is the case left: an invited signup that stopped
 * before the end of its onboarding, or a member that an admin added. Called
 * once, by the room routes.
 */
function useRoomWelcomeAtEntry(roomId: string | null): void {
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const entry =
    roomId === null
      ? "skip"
      : roomEntryWelcome({
          roomId,
          isOnSharedContent: isRoomContentPath(location.pathname),
          user: currentUser,
        });

  const [welcome] = useStore(roomWelcomeStore);

  useEffect(() => {
    if (roomId === null || entry !== "welcome") return;
    // Opened already: by a join, with its discovery channel, or by an entry.
    if (checkedRoomsStore.get().has(roomId)) return;
    // Another room's welcome is on screen: this one waits until it closes.
    if (welcome !== null) return;
    markRoomChecked(roomId);
    roomWelcomeStore.set({ roomId, roomDiscoveryChannel: null });
  }, [roomId, entry, welcome]);
}

/**
 * The welcome asked for, and the room it shows. A room that fails to load
 * leaves the query in error: no sheet opens, the join stays recorded, and the
 * room prompts go on.
 */
function useRequestedWelcome() {
  const [welcome, setWelcome] = useStore(roomWelcomeStore);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    welcome === null ? false : { param: { id: welcome.roomId } }
  );
  // A room that fails to load drops its request, which would otherwise hold
  // every other welcome back. The room stays checked: no retry loop at the
  // entry, and the next session shows the sheet, since nothing recorded it.
  useEffect(() => {
    if (roomQuery.state === "error") setWelcome(null);
  }, [roomQuery.state, setWelcome]);
  return { welcome, setWelcome, roomQuery };
}

/** Whether the welcome sheet is on screen: the room prompts wait for it. */
function useIsRoomWelcomeOpen(): boolean {
  const { roomQuery } = useRequestedWelcome();
  return roomQuery.state === "pending" || roomQuery.state === "success";
}

/**
 * Renders the welcome `openRoomWelcome` asked for, once the room is loaded:
 * a bottom sheet on a phone, a dialog on a desktop. Mounted once, app-wide,
 * like the settings sheet.
 */
function RoomWelcomeHost(): React.JSX.Element | null {
  const { t } = useTranslation();
  const { isDesktop } = useIsDesktopView();
  const { welcome, setWelcome, roomQuery } = useRequestedWelcome();
  if (welcome === null || roomQuery.state !== "success") return null;
  const roomId = welcome.roomId;
  return (
    <RoomWelcomeSheet
      room={roomQuery.body}
      roomDiscoveryChannel={welcome.roomDiscoveryChannel}
      presentation={isDesktop ? "dialog" : "sheet"}
      isOpen
      onClose={() => {
        setWelcome(null);
        // The member saw the room disclaimer: the next entry shows nothing
        // (REQ-14). The room settings keep it until the member leaves.
        apiClient
          .patch("/webapp/private/users/room/:roomId/settings", {
            param: { roomId },
            json: { disclaimer: true },
          })
          .then(reloadCurrentUser)
          .catch(() => {
            displayToast(t("Something went wrong. Please try again."), "error");
          });
      }}
    />
  );
}

export {
  RoomWelcomeHost,
  clearRoomWelcome,
  openRoomWelcome,
  useIsRoomWelcomeOpen,
  useRoomWelcomeAtEntry,
};
